/**
 * What a canvas entity is. Only two things are selectable on the canvas without
 * the inspector's scene tree, and telling them apart decides both the URL that
 * copy-link builds and the route that navigating into one goes to.
 *
 * Carried alongside the id everywhere a selection travels, because every writer
 * already knows it - the overlays hold the node, the inspector holds the
 * element - while every reader downstream would otherwise have to go back to
 * the file's Section list to ask.
 */
export type CanvasEntityKind = 'frame' | 'section'

export type GoToArtboardEventActionType =
  | 'keyPressEnter'
  | 'doubleClick'
  | 'CTA'
  | 'layerListAction'
  | 'contextMenuAction'

/**
 * How the canvas is being driven, which decides who owns the pointer.
 *
 * - `view` - the overlay divs are the hit-test. A click anywhere on an entity
 *   selects it, and CSS paints the hover.
 * - `inspect` - the *scene* is the hit-test: the canvas container's
 *   mousemove/click walk the element tree, so the overlays must stay out of the
 *   way. They carry no body handlers, letting events bubble through to the
 *   canvas, and the header is the single deliberate exception.
 * - `disabled` - no interaction at all (annotations being placed, canvas
 *   interactions switched off).
 */
export type EntityOverlayMode = 'view' | 'inspect' | 'disabled'

/**
 * Which mode the canvas is in, in precedence order.
 *
 * Interactions being switched off wins over everything - the left panel's
 * resizer and an open context menu both turn them off, and neither wants the
 * overlays reacting to the pointer underneath. Inspecting comes next, because
 * the scene hit-test has to own the pointer whenever the Inspect tab is open.
 * Placing a draft annotation is last and also means `disabled`: the pointer
 * belongs to the pin being dropped, not to the entities beneath it.
 */
export function toEntityOverlayMode({
  isCanvasInteractionsEnabled,
  isInspectorActive,
  isPlacingDraftAnnotation,
}: {
  isCanvasInteractionsEnabled: boolean
  isInspectorActive: boolean
  isPlacingDraftAnnotation: boolean
}): EntityOverlayMode {
  if (!isCanvasInteractionsEnabled) return 'disabled'
  if (isInspectorActive) return 'inspect'
  if (isPlacingDraftAnnotation) return 'disabled'

  return 'view'
}

/**
 * What an entity overlay needs from the rest of the application, resolved once
 * for the whole list and stable across renders.
 *
 * Deliberately not resolved per entity. These reach into the inspector and into
 * routing, and the inspector's hovered element comes from a context that
 * changes on every mouse move across the canvas - subscribing to it from each
 * overlay re-rendered all of them on every move, and defeated the `React.memo`
 * on the overlay as well, since context updates bypass it.
 *
 * Nothing that changes with the selection or the hover belongs in here: this
 * object is passed to every overlay, so anything unstable would fail that memo
 * just as surely. Those arrive as already-resolved `isSelected` / `isHovered`
 * booleans instead.
 */
export type EntityInteractions = {
  setHoveredEntityUUID: (uuid: string) => void
  selectEntity: (uuid: string, kind: CanvasEntityKind) => void
  navigateToEntity: (
    uuid: string,
    kind: CanvasEntityKind,
    actionType: GoToArtboardEventActionType
  ) => void
}

/** The overlay elements, keyed by entity id, used to tell a click on an entity from a click on the canvas behind it. */
export type FramesDict = Record<string, HTMLDivElement | null>
